import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bug, Copy, Link2, ListChecks, MessageSquare, Newspaper, Ruler, Sparkles, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatDuration, plainToRichText } from '@kanbix/shared';
import { ItemKeyLink, UserChip } from '@/components/item-bits';
import { Badge, Button, Card, Dialog, Field, Input, Menu, MenuContent, MenuItem, MenuLabel, MenuTrigger, Select, Spinner, Textarea } from '@/components/ui';
import { errorMessage, http, qs } from '@/lib/api';
import { useComponents, useReleases } from '@/lib/queries';
import type { ItemDetail } from '@/lib/types';
import { cn, fmtDate } from '@/lib/utils';

/*
 * Phase 2 AI helpers (PRD §4.12). Every result is a suggestion: nothing is written until the user
 * presses an explicit Apply / Save / Create button, which calls the normal (audited) API.
 */

type Features = Record<string, boolean> | undefined;

const AiNote = ({ children = 'AI suggestion. Review before applying.' }: { children?: React.ReactNode }) => (
  <div className="flex items-center gap-1.5 text-xs text-subtle">
    <Sparkles className="h-3.5 w-3.5 text-brand" /> {children}
  </div>
);

function Loading({ label = 'Thinking…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-6 text-sm text-subtle">
      <Spinner className="h-4 w-4" /> {label}
    </div>
  );
}

const onErr = (e: unknown) => toast.error(errorMessage(e));

// =================================================================================================
// Work item: quality, triage, estimate, links, test cases, comment assist, catch-up
// =================================================================================================

type ItemTool = 'quality' | 'triage' | 'estimate' | 'links' | 'test-cases' | 'comment' | 'catch-up';

interface Quality {
  verdict: 'good' | 'needs_work';
  issues: { kind: string; text: string }[];
  splitSuggestions: string[];
}
interface Triage {
  componentId: string | null;
  priority: string | null;
  releaseId: string | null;
  devOwnerId: string | null;
  qaOwnerId: string | null;
  reasons: string[];
}
interface Estimate {
  storyPoints?: number | null;
  hours?: number | null;
  rationale: string;
  comparables: { key: string; title: string; storyPoints: number | null; spentMin: number }[];
}
interface LinkSuggestion {
  key: string;
  title: string;
  type: string;
  reason: string;
}
export interface DraftCase {
  title: string;
  preconditions: string;
  steps: { step: string; expected: string }[];
  priority: string;
  isCritical: boolean;
  sourceRef?: string | null;
}

/** "AI" menu on the item header; each entry opens a review dialog. */
export function ItemAiMenu({ item, projectKey, features, canEdit, onChanged }: { item: ItemDetail; projectKey?: string; features: Features; canEdit: boolean; onChanged: () => void }) {
  const [tool, setTool] = useState<ItemTool | null>(null);
  if (!features) return null;
  const f = (k: string) => features[k] !== false;
  const entries: { key: ItemTool; label: string; icon: React.ReactNode; on: boolean }[] = [
    { key: 'quality', label: 'Check story quality', icon: <ListChecks className="h-4 w-4" />, on: f('quality_check') && item.type.level >= 2 },
    { key: 'triage', label: 'Suggest triage', icon: <Wand2 className="h-4 w-4" />, on: f('triage_suggest') && canEdit },
    { key: 'estimate', label: 'Suggest estimate', icon: <Ruler className="h-4 w-4" />, on: f('estimate_suggest') && canEdit },
    { key: 'links', label: 'Find related items', icon: <Link2 className="h-4 w-4" />, on: f('link_suggest') },
    { key: 'test-cases', label: 'Generate test cases', icon: <ListChecks className="h-4 w-4" />, on: f('test_case_gen') && item.type.key === 'story' },
    { key: 'comment', label: 'Summarise / reply / handoff', icon: <MessageSquare className="h-4 w-4" />, on: f('comment_assist') },
    { key: 'catch-up', label: 'Catch me up', icon: <Newspaper className="h-4 w-4" />, on: f('catch_up') },
  ];
  const visible = entries.filter((e) => e.on);
  if (!visible.length) return null;
  const close = () => setTool(null);
  return (
    <>
      <Menu>
        <MenuTrigger asChild>
          <Button variant="outline" size="sm" aria-label="AI tools">
            <Sparkles className="h-3.5 w-3.5 text-brand" /> AI
          </Button>
        </MenuTrigger>
        <MenuContent>
          <MenuLabel>Suggestions for {item.key}</MenuLabel>
          {visible.map((e) => (
            <MenuItem key={e.key} onSelect={() => setTool(e.key)}>
              {e.icon} {e.label}
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>
      {tool === 'quality' && <QualityDialog itemKey={item.key} onClose={close} />}
      {tool === 'triage' && <TriageDialog item={item} projectKey={projectKey} onClose={close} onDone={onChanged} />}
      {tool === 'estimate' && <EstimateDialog item={item} onClose={close} onDone={onChanged} />}
      {tool === 'links' && <LinksDialog itemKey={item.key} canEdit={canEdit} onClose={close} onDone={onChanged} />}
      {tool === 'test-cases' && projectKey && <TestCasesDialog source={{ kind: 'item', key: item.key }} projectKey={projectKey} onClose={close} onDone={onChanged} />}
      {tool === 'comment' && <CommentAssistDialog itemKey={item.key} onClose={close} />}
      {tool === 'catch-up' && <CatchUpDialog scope={{ kind: 'item', id: item.key }} onClose={close} />}
    </>
  );
}

function QualityDialog({ itemKey, onClose }: { itemKey: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ['ai-quality', itemKey], queryFn: () => http.post<Quality>(`/ai/items/${itemKey}/suggest/quality`), staleTime: Infinity, retry: false });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Story quality · ${itemKey}`}>
      {q.isLoading && <Loading />}
      {q.error && <div className="text-sm text-danger">{errorMessage(q.error)}</div>}
      {q.data && (
        <div className="space-y-3 text-sm">
          <Badge className={q.data.verdict === 'good' ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning'}>{q.data.verdict === 'good' ? 'Looks ready' : 'Needs work'}</Badge>
          {!!q.data.issues.length && (
            <ul className="space-y-1">
              {q.data.issues.map((i, n) => (
                <li key={n} className="flex gap-2">
                  <Badge>{i.kind.replace(/_/g, ' ')}</Badge>
                  <span>{i.text}</span>
                </li>
              ))}
            </ul>
          )}
          {!!q.data.splitSuggestions.length && (
            <div>
              <div className="mb-1 text-xs font-medium text-subtle">Could be split into</div>
              <ul className="list-disc pl-5">
                {q.data.splitSuggestions.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          )}
          <AiNote>Advisory only — edit the description yourself.</AiNote>
        </div>
      )}
    </Dialog>
  );
}

function TriageDialog({ item, projectKey, onClose, onDone }: { item: ItemDetail; projectKey?: string; onClose: () => void; onDone: () => void }) {
  const { data: components } = useComponents(projectKey);
  const { data: releases } = useReleases(projectKey);
  const q = useQuery({ queryKey: ['ai-triage', item.key], queryFn: () => http.post<Triage>(`/ai/items/${item.key}/suggest/triage`), staleTime: Infinity, retry: false });
  const [skip, setSkip] = useState<Set<string>>(new Set());
  const s = q.data;
  const rows: { key: keyof Triage; label: string; value: React.ReactNode }[] = s
    ? ([
        s.componentId && { key: 'componentId', label: 'Component', value: components?.find((c) => c.id === s.componentId)?.name ?? '—' },
        s.priority && { key: 'priority', label: 'Priority', value: s.priority },
        s.releaseId && { key: 'releaseId', label: 'Release', value: releases?.find((r) => r.id === s.releaseId)?.name ?? '—' },
        s.devOwnerId && { key: 'devOwnerId', label: 'Dev owner', value: <UserChip userId={s.devOwnerId} /> },
        s.qaOwnerId && { key: 'qaOwnerId', label: 'QA owner', value: <UserChip userId={s.qaOwnerId} /> },
      ].filter(Boolean) as { key: keyof Triage; label: string; value: React.ReactNode }[])
    : [];
  const apply = useMutation({
    mutationFn: async () => {
      const on = (k: keyof Triage) => !!s?.[k] && !skip.has(k);
      const patch: Record<string, unknown> = {};
      if (on('componentId')) patch.componentId = s!.componentId;
      if (on('priority')) patch.priority = s!.priority;
      if (on('releaseId')) patch.releaseId = s!.releaseId;
      if (Object.keys(patch).length) await http.patch(`/items/${item.key}`, { version: item.version, ...patch });
      const roles: Record<string, string> = {};
      if (on('devOwnerId')) roles.dev_owner = s!.devOwnerId!;
      if (on('qaOwnerId')) roles.qa_owner = s!.qaOwnerId!;
      if (Object.keys(roles).length) await http.put(`/items/${item.key}/roles`, { roles });
    },
    onSuccess: () => {
      toast.success('Triage applied');
      onDone();
      onClose();
    },
    onError: onErr,
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Triage suggestion · ${item.key}`}
      footer={
        <Button onClick={() => apply.mutate()} loading={apply.isPending} disabled={!rows.some((r) => !skip.has(r.key))}>
          Apply selected
        </Button>
      }
    >
      {q.isLoading && <Loading />}
      {q.error && <div className="text-sm text-danger">{errorMessage(q.error)}</div>}
      {s && (
        <div className="space-y-3 text-sm">
          {!rows.length && <div className="text-subtle">No confident suggestion.</div>}
          {rows.map((r) => (
            <label key={r.key} className="flex items-center gap-2">
              <input type="checkbox" checked={!skip.has(r.key)} onChange={(e) => setSkip((x) => (e.target.checked ? new Set([...x].filter((k) => k !== r.key)) : new Set([...x, r.key])))} />
              <span className="w-24 text-subtle">{r.label}</span>
              <span>{r.value}</span>
            </label>
          ))}
          {!!s.reasons.length && (
            <ul className="list-disc pl-5 text-xs text-subtle">
              {s.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
          <AiNote>Owners are only suggested from people available today (capacity and leave are checked).</AiNote>
        </div>
      )}
    </Dialog>
  );
}

function EstimateDialog({ item, onClose, onDone }: { item: ItemDetail; onClose: () => void; onDone: () => void }) {
  const q = useQuery({ queryKey: ['ai-estimate', item.key], queryFn: () => http.post<Estimate>(`/ai/items/${item.key}/suggest/estimate`), staleTime: Infinity, retry: false });
  const apply = useMutation({
    mutationFn: () => {
      const patch: Record<string, unknown> = {};
      if (q.data?.storyPoints != null) patch.storyPoints = q.data.storyPoints;
      if (q.data?.hours != null) patch.originalEstimateMin = Math.round(q.data.hours * 60);
      return http.patch(`/items/${item.key}`, { version: item.version, ...patch });
    },
    onSuccess: () => {
      toast.success('Estimate applied');
      onDone();
      onClose();
    },
    onError: onErr,
  });
  const has = q.data && (q.data.storyPoints != null || q.data.hours != null);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Estimate · ${item.key}`}
      footer={
        <Button onClick={() => apply.mutate()} loading={apply.isPending} disabled={!has}>
          Apply estimate
        </Button>
      }
    >
      {q.isLoading && <Loading />}
      {q.error && <div className="text-sm text-danger">{errorMessage(q.error)}</div>}
      {q.data && (
        <div className="space-y-3 text-sm">
          <div className="flex gap-4">
            <div>
              <div className="text-xs text-subtle">Story points</div>
              <div className="text-lg font-semibold">{q.data.storyPoints ?? '—'}</div>
            </div>
            <div>
              <div className="text-xs text-subtle">Hours</div>
              <div className="text-lg font-semibold">{q.data.hours ?? '—'}</div>
            </div>
          </div>
          {q.data.rationale && <p>{q.data.rationale}</p>}
          {!!q.data.comparables.length && (
            <Card className="divide-y divide-border">
              {q.data.comparables.map((c) => (
                <div key={c.key} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                  <ItemKeyLink itemKey={c.key} />
                  <span className="min-w-0 flex-1 truncate">{c.title}</span>
                  <span className="text-subtle">
                    {c.storyPoints != null ? `${c.storyPoints} pts` : ''} {c.spentMin ? `· ${formatDuration(c.spentMin)} spent` : ''}
                  </span>
                </div>
              ))}
            </Card>
          )}
          {!has && <div className="text-subtle">Not enough similar completed work to estimate from.</div>}
          <AiNote>Based on similar completed items.</AiNote>
        </div>
      )}
    </Dialog>
  );
}

function LinksDialog({ itemKey, canEdit, onClose, onDone }: { itemKey: string; canEdit: boolean; onClose: () => void; onDone: () => void }) {
  const q = useQuery({ queryKey: ['ai-links', itemKey], queryFn: () => http.post<{ links: LinkSuggestion[] }>(`/ai/items/${itemKey}/suggest/links`), staleTime: Infinity, retry: false });
  const [added, setAdded] = useState<Set<string>>(new Set());
  const add = useMutation({
    mutationFn: (l: LinkSuggestion) => http.post(`/items/${itemKey}/links`, { targetKey: l.key, type: l.type }),
    onSuccess: (_r, l) => {
      setAdded((s) => new Set([...s, l.key]));
      onDone();
    },
    onError: onErr,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Related items · ${itemKey}`} wide>
      {q.isLoading && <Loading />}
      {q.error && <div className="text-sm text-danger">{errorMessage(q.error)}</div>}
      {q.data && (
        <div className="space-y-2 text-sm">
          {!q.data.links.length && <div className="text-subtle">No likely related items found.</div>}
          {q.data.links.map((l) => (
            <div key={l.key} className="flex items-center gap-2">
              <Badge>{l.type.replace(/_/g, ' ')}</Badge>
              <ItemKeyLink itemKey={l.key} />
              <div className="min-w-0 flex-1">
                <div className="truncate">{l.title}</div>
                <div className="text-xs text-subtle">{l.reason}</div>
              </div>
              {canEdit && (
                <Button size="sm" variant="outline" disabled={added.has(l.key)} onClick={() => add.mutate(l)}>
                  {added.has(l.key) ? 'Linked' : 'Add link'}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Dialog>
  );
}

/** Generated test cases are editable and individually selectable before saving (FR-AI-10, FR-AI-46). */
export function TestCasesDialog({ source, projectKey, onClose, onDone }: { source: { kind: 'item'; key: string } | { kind: 'document'; id: string }; projectKey: string; onClose: () => void; onDone?: () => void }) {
  const url = source.kind === 'item' ? `/ai/items/${source.key}/test-cases` : `/ai/documents/${source.id}/test-cases`;
  const [cases, setCases] = useState<(DraftCase & { keep: boolean })[] | null>(null);
  const gen = useQuery({
    queryKey: ['ai-test-cases', url],
    queryFn: async () => {
      const r = await http.post<{ cases: DraftCase[] }>(url);
      setCases(r.cases.map((c) => ({ ...c, keep: true })));
      return r;
    },
    staleTime: Infinity,
    retry: false,
  });
  const save = useMutation({
    mutationFn: () => http.post<{ created: string[] }>('/ai/test-cases/save', { projectKey, storyKey: source.kind === 'item' ? source.key : undefined, cases: cases!.filter((c) => c.keep).map(({ keep: _k, sourceRef: _s, ...c }) => c) }),
    onSuccess: (r) => {
      toast.success(`Saved ${r.created.length} test case(s)`, { description: r.created.join(', ') });
      onDone?.();
      onClose();
    },
    onError: onErr,
  });
  const set = (i: number, patch: Partial<DraftCase & { keep: boolean }>) => setCases((cs) => cs!.map((c, n) => (n === i ? { ...c, ...patch } : c)));
  const kept = cases?.filter((c) => c.keep).length ?? 0;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Generated test cases"
      description="Saved cases are marked AI-assisted and linked for coverage. Untick any you don't want."
      wide
      footer={
        <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!kept}>
          Save {kept} case(s)
        </Button>
      }
    >
      {gen.isLoading && <Loading label="Drafting cases from the acceptance criteria…" />}
      {gen.error && <div className="text-sm text-danger">{errorMessage(gen.error)}</div>}
      <div className="max-h-[60vh] space-y-3 overflow-auto">
        {cases?.map((c, i) => (
          <Card key={i} className={cn('space-y-2 p-3', !c.keep && 'opacity-50')}>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={c.keep} onChange={(e) => set(i, { keep: e.target.checked })} aria-label="Keep case" />
              <Input value={c.title} onChange={(e) => set(i, { title: e.target.value })} className="h-8 flex-1" />
              <Select value={c.priority} onChange={(e) => set(i, { priority: e.target.value })} className="h-8 w-28">
                {['critical', 'high', 'medium', 'low'].map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </Select>
              <label className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={c.isCritical} onChange={(e) => set(i, { isCritical: e.target.checked })} /> critical
              </label>
            </div>
            {c.preconditions && <div className="text-xs text-subtle">Pre: {c.preconditions}</div>}
            <ol className="list-decimal space-y-0.5 pl-5 text-xs">
              {c.steps.map((s, n) => (
                <li key={n}>
                  {s.step} {s.expected && <span className="text-subtle">→ {s.expected}</span>}
                </li>
              ))}
            </ol>
            {c.sourceRef && <div className="text-[11px] text-subtle">Source: {c.sourceRef}</div>}
          </Card>
        ))}
      </div>
    </Dialog>
  );
}

function CommentAssistDialog({ itemKey, onClose }: { itemKey: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'summary' | 'reply' | 'handoff'>('summary');
  const [text, setText] = useState('');
  const gen = useMutation({ mutationFn: (m: typeof mode) => http.post<{ text: string }>(`/ai/items/${itemKey}/comment-assist`, { mode: m }), onSuccess: (r) => setText(r.text), onError: onErr });
  const post = useMutation({
    mutationFn: () => http.post(`/items/${itemKey}/comments`, { body: plainToRichText(text) }),
    onSuccess: () => {
      toast.success('Comment posted');
      void qc.invalidateQueries({ queryKey: ['comments', itemKey] });
      onClose();
    },
    onError: onErr,
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Comment assist · ${itemKey}`}
      wide
      footer={
        <>
          <Button variant="ghost" disabled={!text} onClick={() => void navigator.clipboard.writeText(text).then(() => toast.success('Copied'))}>
            <Copy className="h-3.5 w-3.5" /> Copy
          </Button>
          <Button onClick={() => post.mutate()} loading={post.isPending} disabled={!text.trim()}>
            Post as my comment
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {(['summary', 'reply', 'handoff'] as const).map((m) => (
            <Button key={m} size="sm" variant={mode === m ? 'secondary' : 'outline'} onClick={() => setMode(m)}>
              {m === 'summary' ? 'Summarise thread' : m === 'reply' ? 'Draft a reply' : 'Handoff note'}
            </Button>
          ))}
          <Button size="sm" onClick={() => gen.mutate(mode)} loading={gen.isPending}>
            <Sparkles className="h-3.5 w-3.5" /> Generate
          </Button>
        </div>
        <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder="Generate, then edit before posting." />
        <AiNote>Posted under your name; edit freely first.</AiNote>
      </div>
    </Dialog>
  );
}

// =================================================================================================
// Catch me up (FR-AI-21)
// =================================================================================================

interface CatchUp {
  summary: string;
  highlights: string[];
  needsYou: string[];
  since: string;
}

export function CatchUpDialog({ scope, onClose }: { scope: { kind: 'me' | 'item' | 'channel' | 'release'; id?: string }; onClose: () => void }) {
  const [hours, setHours] = useState(24);
  const q = useQuery({
    queryKey: ['ai-catch-up', scope.kind, scope.id, hours],
    queryFn: () => http.post<CatchUp>('/ai/catch-up', { ...scope, since: new Date(Date.now() - hours * 3600_000).toISOString() }),
    staleTime: 60_000,
    retry: false,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Catch me up" wide>
      <div className="space-y-3 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-subtle">Since</span>
          <Select value={hours} onChange={(e) => setHours(Number(e.target.value))} className="h-8 w-36">
            <option value={8}>8 hours ago</option>
            <option value={24}>yesterday</option>
            <option value={72}>3 days ago</option>
            <option value={168}>a week ago</option>
          </Select>
        </div>
        {q.isLoading && <Loading />}
        {q.error && <div className="text-danger">{errorMessage(q.error)}</div>}
        {q.data && (
          <>
            <p>{q.data.summary}</p>
            {!!q.data.needsYou.length && (
              <Card className="space-y-1 border-warning/40 p-3">
                <div className="text-xs font-semibold text-warning">Needs you</div>
                <ul className="list-disc pl-5">
                  {q.data.needsYou.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </Card>
            )}
            {!!q.data.highlights.length && (
              <ul className="list-disc space-y-0.5 pl-5">
                {q.data.highlights.map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ul>
            )}
            <AiNote>Summary of activity you can see.</AiNote>
          </>
        )}
      </div>
    </Dialog>
  );
}

export function CatchUpButton({ scope, label = 'Catch me up', size = 'sm', variant = 'outline' }: { scope: { kind: 'me' | 'item' | 'channel' | 'release'; id?: string }; label?: string; size?: 'sm' | 'icon'; variant?: 'outline' | 'ghost' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size={size} variant={variant} onClick={() => setOpen(true)} title="Catch me up" aria-label="Catch me up">
        <Newspaper className="h-3.5 w-3.5" /> {size !== 'icon' && label}
      </Button>
      {open && <CatchUpDialog scope={scope} onClose={() => setOpen(false)} />}
    </>
  );
}

// =================================================================================================
// Test runs and builds: failure clusters (FR-AI-11/12), retest scope (FR-AI-13), notes (FR-AI-14)
// =================================================================================================

interface Cluster {
  resultIds: string[];
  title: string;
  rationale: string;
  existingBugKey: string | null;
  bug: { title: string; severity: string; priority: string; description: string };
}

export function ClusterFailuresButton({ runId, caseTitle, onDone }: { runId: string; caseTitle: (resultId: string) => string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<Record<number, string>>({});
  const q = useQuery({ queryKey: ['ai-clusters', runId], queryFn: () => http.post<{ clusters: Cluster[] }>(`/ai/test-runs/${runId}/cluster-failures`), enabled: open, staleTime: Infinity, retry: false });
  const act = useMutation({
    mutationFn: ({ c }: { c: Cluster; i: number }) =>
      http.post<{ bug: { key: string } }>('/test-results/defects', c.existingBugKey ? { mode: 'link', resultIds: c.resultIds, bugKey: c.existingBugKey } : { mode: 'create', resultIds: c.resultIds, title: c.bug.title, severity: c.bug.severity, priority: c.bug.priority }),
    onSuccess: (r, { i }) => {
      setDone((d) => ({ ...d, [i]: r.bug.key }));
      onDone();
    },
    onError: onErr,
  });
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Sparkles className="h-3.5 w-3.5 text-brand" /> Group failures
      </Button>
      {open && (
        <Dialog open onOpenChange={setOpen} title="Failures grouped by likely cause" description="One bug per group. Steps, build and owners are filled in when the bug is created." wide>
          {q.isFetching && <Loading label="Grouping failed and blocked results…" />}
          {q.error && <div className="text-sm text-danger">{errorMessage(q.error)}</div>}
          <div className="max-h-[60vh] space-y-3 overflow-auto">
            {q.data?.clusters.map((c, i) => (
              <Card key={i} className="space-y-2 p-3 text-sm">
                <div className="flex items-center gap-2">
                  <Bug className="h-4 w-4 text-danger" />
                  <span className="font-medium">{c.title}</span>
                  <Badge>{c.resultIds.length} result(s)</Badge>
                  <div className="ml-auto">
                    {done[i] ? (
                      <ItemKeyLink itemKey={done[i]} />
                    ) : (
                      <Button size="sm" onClick={() => act.mutate({ c, i })} loading={act.isPending && act.variables?.i === i}>
                        {c.existingBugKey ? `Link to ${c.existingBugKey}` : 'Create bug'}
                      </Button>
                    )}
                  </div>
                </div>
                {c.rationale && <div className="text-xs text-subtle">{c.rationale}</div>}
                <ul className="list-disc pl-5 text-xs">
                  {c.resultIds.map((id) => (
                    <li key={id}>{caseTitle(id)}</li>
                  ))}
                </ul>
                {!c.existingBugKey && (
                  <div className="text-xs text-subtle">
                    New bug: “{c.bug.title}” · {c.bug.severity} · {c.bug.priority}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </Dialog>
      )}
    </>
  );
}

export function RetestScopeCard({ buildId }: { buildId: string }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ['ai-retest', buildId], queryFn: () => http.get<{ cases: { id: string; key: string; title: string; reason: string }[]; summary: string }>(`/ai/builds/${buildId}/retest-scope`), enabled: open, retry: false });
  return (
    <Card className="space-y-2 p-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold">Suggested retest scope</h2>
        {!open && (
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setOpen(true)}>
            <Sparkles className="h-3.5 w-3.5 text-brand" /> Suggest
          </Button>
        )}
      </div>
      {q.isLoading && <Loading />}
      {q.error && <div className="text-sm text-danger">{errorMessage(q.error)}</div>}
      {q.data && (
        <>
          {q.data.summary && <p className="text-sm">{q.data.summary}</p>}
          <div className="max-h-72 divide-y divide-border overflow-auto text-sm">
            {q.data.cases.map((c) => (
              <div key={c.id} className="flex gap-2 py-1.5">
                <span className="w-20 shrink-0 font-mono text-xs text-subtle">{c.key}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{c.title}</div>
                  <div className="text-xs text-subtle">{c.reason}</div>
                </div>
              </div>
            ))}
          </div>
          <AiNote>Use this list when building a retest suite; nothing is created automatically.</AiNote>
        </>
      )}
    </Card>
  );
}

export function BuildNotesButton({ buildId }: { buildId: string }) {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState<'customer' | 'internal'>('customer');
  const gen = useMutation({ mutationFn: () => http.post<{ title: string; markdown: string }>(`/ai/builds/${buildId}/release-notes`, { audience }), onError: onErr });
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Sparkles className="h-3.5 w-3.5 text-brand" /> Release notes
      </Button>
      {open && (
        <Dialog
          open
          onOpenChange={setOpen}
          title="Draft release notes for this build"
          wide
          footer={
            gen.data && (
              <Button variant="outline" onClick={() => void navigator.clipboard.writeText(gen.data.markdown).then(() => toast.success('Copied'))}>
                <Copy className="h-3.5 w-3.5" /> Copy markdown
              </Button>
            )
          }
        >
          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <Field label="Audience">
                <Select value={audience} onChange={(e) => setAudience(e.target.value as typeof audience)} className="h-8 w-36">
                  <option value="customer">Customers</option>
                  <option value="internal">Internal</option>
                </Select>
              </Field>
              <Button size="sm" onClick={() => gen.mutate()} loading={gen.isPending}>
                Generate
              </Button>
            </div>
            {gen.data && <Textarea rows={14} readOnly value={gen.data.markdown} className="font-mono text-xs" />}
          </div>
        </Dialog>
      )}
    </>
  );
}

// =================================================================================================
// Narratives: lineage (FR-AI-15) and go/no-go (FR-AI-16)
// =================================================================================================

export function NarrativeButton({ url, label }: { url: string; label: string }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ['ai-narrative', url], queryFn: () => http.post<{ narrative: string; citedMetrics: string[] }>(url), enabled: open, staleTime: 5 * 60_000, retry: false });
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Sparkles className="h-3.5 w-3.5 text-brand" /> {label}
      </Button>
      {open && (
        <Dialog open onOpenChange={setOpen} title={label} wide>
          {q.isLoading && <Loading />}
          {q.error && <div className="text-sm text-danger">{errorMessage(q.error)}</div>}
          {q.data && (
            <div className="space-y-3 text-sm">
              <p className="whitespace-pre-wrap">{q.data.narrative}</p>
              {!!q.data.citedMetrics.length && (
                <div className="flex flex-wrap gap-1">
                  {q.data.citedMetrics.map((m) => (
                    <Badge key={m}>{m}</Badge>
                  ))}
                </div>
              )}
              <AiNote>Written from the recorded metrics only; the decision stays with the approvers.</AiNote>
            </div>
          )}
        </Dialog>
      )}
    </>
  );
}

// =================================================================================================
// Time: natural-language logging (FR-AI-18) and timesheet suggestions (FR-AI-19)
// =================================================================================================

interface TimeProposal {
  itemKey: string;
  title: string;
  durationMin: number | null;
  workDate: string;
  activityType: string;
  description: string;
}

const logWork = (p: { itemKey: string; durationMin: number; workDate: string; activityType?: string; description?: string }) =>
  http.post(`/items/${p.itemKey}/worklogs`, { duration: `${p.durationMin}m`, workDate: p.workDate, activityType: p.activityType ?? 'development', description: p.description || undefined });

export function NaturalTimeLog({ onDone }: { onDone?: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [p, setP] = useState<TimeProposal | null>(null);
  const parse = useMutation({ mutationFn: () => http.post<TimeProposal>('/ai/time/parse', { text }), onSuccess: setP, onError: onErr });
  const save = useMutation({
    mutationFn: () => logWork({ ...p!, durationMin: p!.durationMin! }),
    onSuccess: () => {
      toast.success(`Logged ${formatDuration(p!.durationMin!)} on ${p!.itemKey}`);
      setP(null);
      setText('');
      void qc.invalidateQueries({ queryKey: ['worklogs'] });
      void qc.invalidateQueries({ queryKey: ['timesheet'] });
      onDone?.();
    },
    onError: onErr,
  });
  return (
    <Card className="space-y-2 p-3">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) parse.mutate();
        }}
      >
        <Sparkles className="mt-2 h-4 w-4 shrink-0 text-brand" />
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Log time in words — e.g. “2h on KBX-12 code review yesterday”" className="h-9" />
        <Button size="sm" type="submit" loading={parse.isPending} disabled={!text.trim()}>
          Read
        </Button>
      </form>
      {p && (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 p-2 text-sm">
          <ItemKeyLink itemKey={p.itemKey} />
          <span className="min-w-0 flex-1 truncate">{p.title}</span>
          <Input
            className="h-8 w-20"
            value={p.durationMin ?? ''}
            onChange={(e) => setP({ ...p, durationMin: Number(e.target.value) || null })}
            aria-label="Minutes"
            title="Minutes"
          />
          <span className="text-xs text-subtle">min</span>
          <Input type="date" className="h-8 w-36" value={p.workDate} onChange={(e) => setP({ ...p, workDate: e.target.value })} />
          <Badge>{p.activityType.replace(/_/g, ' ')}</Badge>
          <Button size="sm" variant="ghost" onClick={() => setP(null)}>
            Discard
          </Button>
          <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!p.durationMin}>
            Log it
          </Button>
        </div>
      )}
    </Card>
  );
}

interface Suggestions {
  days: { date: string; loggedMin: number; capacityMin: number; missing: boolean }[];
  suggestions: { itemKey: string; title: string; date: string; minutes: number; reason: string }[];
}

export function TimesheetSuggestions({ week, editable }: { week: string; editable: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const q = useQuery({ queryKey: ['ai-timesheet', week], queryFn: () => http.get<Suggestions>(`/ai/timesheets/suggestions${qs({ week })}`), enabled: open, retry: false });
  const add = useMutation({
    mutationFn: (s: Suggestions['suggestions'][number]) => logWork({ itemKey: s.itemKey, durationMin: s.minutes, workDate: s.date }),
    onSuccess: (_r, s) => {
      setAdded((x) => new Set([...x, `${s.itemKey}:${s.date}`]));
      void qc.invalidateQueries({ queryKey: ['timesheet'] });
    },
    onError: onErr,
  });
  const missing = q.data?.days.filter((d) => d.missing) ?? [];
  if (!open)
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Sparkles className="h-3.5 w-3.5 text-brand" /> Suggest entries
      </Button>
    );
  return (
    <Card className="space-y-2 p-3 text-sm">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold">Suggested from your activity</h3>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setOpen(false)}>
          Hide
        </Button>
      </div>
      {q.isLoading && <Loading />}
      {!!missing.length && <div className="text-warning">No time logged on {missing.map((d) => fmtDate(d.date, 'EEE d MMM')).join(', ')}.</div>}
      {q.data && !q.data.suggestions.length && <div className="text-subtle">Nothing to suggest — your logged time matches your activity.</div>}
      {q.data?.suggestions.map((s) => {
        const k = `${s.itemKey}:${s.date}`;
        return (
          <div key={k} className="flex items-center gap-2">
            <span className="w-20 text-xs text-subtle">{fmtDate(s.date, 'EEE d')}</span>
            <ItemKeyLink itemKey={s.itemKey} />
            <span className="min-w-0 flex-1 truncate">{s.title}</span>
            <span className="text-xs text-subtle">{s.reason}</span>
            <span className="w-14 text-right tabular-nums">{formatDuration(s.minutes)}</span>
            {editable && (
              <Button size="sm" variant="outline" disabled={added.has(k)} onClick={() => add.mutate(s)}>
                {added.has(k) ? 'Logged' : 'Log'}
              </Button>
            )}
          </div>
        );
      })}
    </Card>
  );
}

// =================================================================================================
// Semantic search (FR-AI-24)
// =================================================================================================

const TYPE_LABEL: Record<string, string> = { work_item: 'Item', document_section: 'Document', page: 'Page', test_case: 'Test case', transcript_segment: 'Recording' };

export function SemanticResults({ q }: { q: string }) {
  const r = useQuery({ queryKey: ['semantic', q], queryFn: () => http.get<{ type: string; id: string; title: string; snippet: string; score: number; link: string }[]>(`/ai/semantic-search${qs({ q })}`), enabled: q.length > 2, retry: false });
  return (
    <section>
      <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
        <Sparkles className="h-3.5 w-3.5 text-brand" /> By meaning
      </h2>
      {r.isLoading && <Loading label="Searching by meaning…" />}
      {r.error && <div className="text-sm text-subtle">{errorMessage(r.error)}</div>}
      {r.data && (
        <Card className="divide-y divide-border">
          {r.data.map((x) => (
            <Link key={`${x.type}:${x.id}`} to={x.link} className="block px-3 py-2 text-sm hover:bg-muted/40">
              <Badge>{TYPE_LABEL[x.type] ?? x.type}</Badge> <span className="font-medium">{x.title}</span> <span className="text-xs text-subtle">{Math.round(x.score * 100)}%</span>
              <div className="line-clamp-2 text-xs text-subtle">{x.snippet}</div>
            </Link>
          ))}
          {!r.data.length && <div className="px-3 py-2 text-sm text-subtle">No close matches</div>}
        </Card>
      )}
    </section>
  );
}

// =================================================================================================
// Stand-up polish (FR-AI-22)
// =================================================================================================

export function StandupPolishButton({ date, onDraft }: { date: string; onDraft: (d: { yesterday: string; today: string; blockers: string }) => void }) {
  const gen = useMutation({
    mutationFn: () => http.post<{ yesterday: string; today: string; blockers: string }>('/ai/standups/draft', { date }),
    onSuccess: (d) => {
      onDraft(d);
      toast.success('Draft rewritten — review before posting');
    },
    onError: onErr,
  });
  return (
    <Button variant="outline" onClick={() => gen.mutate()} loading={gen.isPending}>
      <Sparkles className="h-3.5 w-3.5 text-brand" /> Polish with AI
    </Button>
  );
}
